import type { BreadcrumbItem } from '@/app/components/atoms/Breadcrumb'
import { route, routes } from '@/app/lib/routing'

export interface PerformanceHeadingsInput {
  readonly name: string
  readonly province: string | null
  /** Etiqueta legible de la categoría (`Teatro`, `Conciertos`…). */
  readonly categoryLabel: string
  /** Slug de la categoría, para el `href` de su miga. */
  readonly categorySlug: string
}

/**
 * H1 de la ficha: `{nombre}[, {provincia}]: fecha y entradas`.
 *
 * Cumple el test de `06` §5.4 — quitarle la marca al title da
 * "Entradas para {nombre}, {provincia}", que NO es este H1: distinta estructura
 * y distinto enfoque, misma keyword.
 */
export function buildPerformanceH1({ name, province }: PerformanceHeadingsInput): string {
  const place = province ? `, ${province}` : ''
  return `${name}${place}: fecha y entradas`
}

/**
 * Migas de pan: `Inicio > {Categoría} > {Provincia} > {nombre}`.
 *
 * La miga de provincia se omite cuando el evento no trae recinto con provincia
 * — hay filas Nivel 5 reales así (eventos online). La última nunca lleva
 * `href` (`06` §5.5, `03` §7).
 */
export function buildPerformanceBreadcrumb({
  name,
  province,
  categoryLabel,
  categorySlug,
}: PerformanceHeadingsInput): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [{ label: 'Inicio', href: routes.home() }]

  if (categorySlug) {
    items.push({ label: categoryLabel, href: route(`/${categorySlug}`) })
  }
  if (province) {
    items.push({ label: province })
  }
  items.push({ label: name })

  return items
}

/**
 * Keywords del patrón Nivel 5. Las secundarias se **complementan** con
 * `seo_meta_keywords` del backend cuando llega: es el punto exacto en que el
 * backend aporta al patrón sin sustituirlo (contracts/seo-pattern-package.md §2.5).
 */
export function buildPerformanceKeywords(
  name: string,
  backendKeywords: string | null | undefined,
): { readonly primary: string; readonly secondary: readonly string[] } {
  const secondary = [`entradas ${name}`, `comprar entradas ${name}`, `fecha ${name}`]

  const extra = (backendKeywords ?? '')
    .split(',')
    .map((keyword) => keyword.trim())
    .filter((keyword) => keyword !== '' && !secondary.includes(keyword))

  return { primary: `entradas ${name}`, secondary: [...secondary, ...extra] }
}
